import type { ComponentType, ReactElement, ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type {
	Direction,
	DraggableStateSnapshot,
	DraggingStyle,
	NotDraggingStyle,
} from 'react-beautiful-dnd';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import type { FieldOperations } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type {
	IntlShapeV2 as IntlShape,
	MessageDescriptorV2 as MessageDescriptor,
} from '@atlassian/jira-intl/src/v2/types.tsx';
import type {
	ProformaReqTypeConfig,
	ProformaReqTypeFieldConfig,
} from '@atlassian/jira-proforma-request-type-config/src/types.tsx';
import { toPackageName } from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import type {
	Avatar,
	PackageName,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type { ProjectId, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import type { Project } from '@atlassian/jira-shared-with-projects/src/common/ui/types.tsx';
import type { FormRestrictionStatus } from '@atlassian/proforma-form-security';
import type {
	DO_NOT_ANIMATE_WIDTH,
	FAILURE,
	Group,
	INIT,
	LOADING,
	SUCCESS,
	SwiftVersion,
} from './constants.tsx';
import type { ExplicitFieldAssociationSettings } from './services/fetch-explicit-field-association/index.tsx';
import type { PageLoadConfig } from './types/analytics.tsx';
import type { ConfirmBeforeSaveProps } from './types/confirm-before-save.tsx';
import type { ExtraDefinerId, IssueLayoutOwner } from './types/issue/issue-layout-owner.tsx';
import type { IssueType, IssueTypeCollection, IssueTypeId } from './types/issue/issue-types.tsx';
import type {
	ActiveLayoutItem,
	AllowedActions,
	AccessControl,
	AssociatedField,
	AvailableLayoutItemGroup,
	BaseLayoutItem,
	Field,
	FieldCategoryConfiguration,
	FieldCategoryWithFields,
	FieldTypeList,
	FieldTypeObj,
	InactiveFieldLayoutItem,
	InactiveLayoutItem,
	InactiveLayoutItemGroup,
	LayoutItem,
	SwiftTabWithLinks,
} from './types/layout-items.tsx';
import type { IssueDataState } from './types/legacy.tsx';
import type { SaveChangesService } from './types/save-changes.tsx';
import type { TemplateIntl } from './types/templates.tsx';
import type { InactiveLayoutItemPanelProps } from './types/ui.tsx';
import type { FieldValidator, Limits, ValidationError } from './types/validation.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { IProject } from './types/project';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { AssociatedField, FieldProvider, HiddenIn } from './types/layout-items';

export type IssueRestriction = {
	restrictionValue: string;
};
export type IssueRestrictionType = {
	defaultRestrictions: IssueRestriction[];
};

export type ProjectFieldCollection = {
	/**
	 * @deprecated Please use `limitFieldsPerProject`
	 */
	limitProjectScopedPerProject: number | null;
	limitFieldsPerProject: number | null;
	/**
	 * Indicates how mane fields server is aware about
	 */
	currentFieldsPerProject: number | null;
	/**
	 * Counter for fields which are added on the client but hasn't been sent to the server just yet.
	 * Initialized with the number of fields on the server.
	 */
	nextFieldsPerProject: number | null;
};

type FieldOptionCollection = {
	limitPerField: number | null;
};

export type FetchAvailableItemsParams = {
	projectContext: ProjectContext;
	// extraDefinerId is used to construct the issueLayoutKey used in the graphQL request
	// It could be issue type id, screen id or request type id depending on product.
	extraDefinerId: ExtraDefinerId;
	pageSize: number;
	queryString: string;
};

export type FetchMoreAvailableItemsParams = {
	projectContext: ProjectContext;
	// extraDefinerId is used to construct the issueLayoutKey used in the graphQL request
	// It could be issue type id, screen id or request type id depending on product.
	extraDefinerId: ExtraDefinerId;
	pageSize: number;
	after: string;
	group: Group;
	queryString: string;
};

export type FetchAvailableItemsService = (
	arg1: FetchAvailableItemsParams,
) => Promise<ReadonlyArray<AvailableLayoutItemGroup>>;

export type FetchMoreAvailableItemsService = (
	arg1: FetchMoreAvailableItemsParams,
) => Promise<ReadonlyArray<AvailableLayoutItemGroup>>;

export type FetchLayoutConfigurationServiceParams = {
	projectContext: ProjectContext;
	extraDefinerId: ExtraDefinerId;
	availableItemsPageSize: number;
	cloudId: string;
};

export type FetchLayoutConfigurationServiceResponse = {
	readonly layoutId: string | null;
	readonly screenName: string;
	readonly issueLayoutOwners: IssueLayoutOwner[];
	readonly projectFieldCollection: ProjectFieldCollection;
	readonly fieldOptionCollection: FieldOptionCollection;
	readonly availableLayoutItemGroups: AvailableLayoutItemGroup[];
	readonly fieldTypes: FieldTypeList;
	readonly activeLayoutItems: ActiveLayoutItem[];
	readonly nonConfigurableFields: string[];
	readonly categoryConfigurations: FieldCategoryConfiguration[];
	readonly tabs: {
		[fieldCategoryName: string]: SwiftTabWithLinks[];
	};
	readonly issueRestriction: IssueRestrictionType | null;
	readonly proformaReqTypeConfig?: ProformaReqTypeConfig;
	/**
	 * List of all projects that are sharing the layout.
	 * This list is used to render "Are you sure?" and "This layout is shared with X projects" dialogs.
	 *
	 * If empty then SWIFT assumes that only the current project is affected.
	 */
	readonly affectedProjects: ReadonlyArray<Project>;
	readonly limits: Limits;

	/**
	 * Configurable actions a user can do in the issue layout editor.
	 *
	 * This is added as a part of the 'DelegatedAdmin' feature, which will allow global admins to grant users permissions for tasks
	 * such as searching and adding existing fields to an issue's layout in the layout editor.
	 *
	 * This feature exclusively impacts CMP projects.
	 *
	 * For more information about 'AllowedActions', please see {@link AllowedActions}
	 *
	 * For more information on 'DelegatedAdmin', please see:
	 * https://hello.atlassian.net/wiki/x/0orlng (One-Pager) and
	 * https://hello.atlassian.net/wiki/x/XnO_ow ('What can users do?' with the new permissions)
	 *
	 * This property is nullable for two main reasons:
	 * - Firstly, it is part of a shared response object used in both TMP and CMP GraphQL queries. Because the 'DelegatedAdmin'
	 *   feature does not include TMP projects, any queries for TMP projects will not be updated to include the new 'allowedActions'.
	 * - Secondly, the rollout of this feature across different CMP project types (JSW, JSM) will happen incrementally
	 *   as each one is implemented. Until the queries for these project types are updated, they won't possess any 'allowedActions'.
	 *
	 * If this property is null and the queried project is a CMP project, it should be assumed that the 'DelegatedAdmin' feature has
	 * not been rolled out for that project type yet. In such instances, the issue layout editor will default to the pre-existing logic
	 * prior to the introduction of the 'DelegatedAdmin' feature.
	 * TODO: Remove this field, it is replaced by accessControl https://hello.jira.atlassian.cloud/browse/DAGC-114
	 */
	readonly allowedActions: AllowedActions | null;

	/**
	 * Access control properties of a given screen. This field influences the page's rendering and functionality
	 * when editing that screen.
	 *
	 * ex. If the conjunction of the fields 'userHasEditLayoutPermission' and 'isScreenIsolated' is true the user will see
	 * the 'admin' sidebar that displays the enabled searchbox which gives the users the capability to search for
	 * existing fields and add them to an issue layout. When it is false, a 'Sidebar' is rendered with a disabled searchbox
	 * and without any additional fields.
	 *
	 * Note:
	 * - This property exclusively impacts CMP projects.
	 * - For TMP project types, THIS PROPERTY WILL NOT BE UTILIZED.
	 * - The value of this property is populated through a GraphQL query retrieving LayoutConfiguration for the issue layout editor.
	 * - The implementation of this feature is being carried out by project type, meaning that their respective GraphQL queries
	 *   will be updated to fetch 'accessControl' upon rollout.
	 */
	accessControl: AccessControl | null;
	/**
	 * https://hello.atlassian.net/wiki/spaces/MOCA/pages/3123207453/Project+Poster+Explicit+Field+Associations+EFA
	 */
	readonly explicitFieldAssociation: ExplicitFieldAssociationSettings;
};

/**
 * ExcludeFromMonitoringError are not going to be reported to Sentry or Splunk as errors,
 * instead they will be silently ignored.
 *
 * If you wish to continue reporting them, please use a regular Error.
 */
export class ExcludeFromMonitoringError extends Error {}

type FetchLayoutConfigurationService = (
	arg1: FetchLayoutConfigurationServiceParams,
) => Promise<FetchLayoutConfigurationServiceResponse>;

// If a product decides to hide a message, let's make sure it is a deliberate feature and not that somebody forgot
export const SHOW_NO_MESSAGE = null;

// All the i18n strings in add-issue-type app
export type AddIssueTypeMessages = {
	modalDialogTitle: MessageDescriptor;
	modalDialogTitleIssueTermRefresh?: MessageDescriptor;
	modalDialogAddTitle: MessageDescriptor;
	modalDialogAddTitleIssueTermRefresh?: MessageDescriptor;
	nameInputLabel: MessageDescriptor;
	nameInputPlaceholder: MessageDescriptor;
	nameTooShortError: MessageDescriptor;
	nameAlreadyExistsError: MessageDescriptor;
	nameTooLongError: MessageDescriptor;
	descriptionInputLabel: MessageDescriptor;
	descriptionInputPlaceholder: MessageDescriptor;
	descriptionInputPlaceholderIssueTermRefresh?: MessageDescriptor;
	descriptionTooLongError: MessageDescriptor;
	screenReaderErrorPrefixForFieldErrorMessage: MessageDescriptor;
	avatarLabel: MessageDescriptor;
	avatarIconName: MessageDescriptor;
	avatarIconNameIssueTermRefresh?: MessageDescriptor;
	changeAvatarAction: MessageDescriptor;
	createNewIssueTypeConfirmButton: MessageDescriptor;
	createNewIssueTypeCancelButton: MessageDescriptor;
	templatesHeader: MessageDescriptor;
	addIssueTypeFromTemplateConfirmButton: MessageDescriptor;
	addIssueTypeFromTemplateCancelButton: MessageDescriptor;
	moveFromTemplateToCreateButton: MessageDescriptor;
	moveFromTemplateToCreateButtonIssueTermRefresh?: MessageDescriptor;
	addIssueTypeSuccessTitle: MessageDescriptor;
	addIssueTypeSuccessDescription: MessageDescriptor;
	addIssueTypeFailureTitle: MessageDescriptor;
	addIssueTypeFailureTitleIssueTermRefresh?: MessageDescriptor;
	addIssueTypeFailureDescription: MessageDescriptor;
	addIssueTypeFailureDescriptionIssueTermRefresh?: MessageDescriptor;
	triggerButton: MessageDescriptor;
	triggerButtonIssueTermRefresh?: MessageDescriptor;
};

export type EditIssueTypeMessages = {
	// project-settings-apps/issue-type-page/flags/index.js
	editIssueTypeSuccessTitle: MessageDescriptor;
	editIssueTypeSuccessDescription: MessageDescriptor;
	backToProject: MessageDescriptor;
	editIssueTypeFailureTitle: MessageDescriptor;
	editIssueTypeFailureDescription: MessageDescriptor;
	// project-settings-apps/issue-type-page/view/main-header/description-inline-input/view.js
	descriptionLabel: MessageDescriptor;
	editDescriptionButtonLabel: MessageDescriptor;
	descriptionPlaceholder: MessageDescriptor;
	// passed into @atlaskit/avatar props as `src=`
	avatarAlt: MessageDescriptor;
	avatarAccessibleAlt: MessageDescriptor;
	avatarAriaLabel: MessageDescriptor;
	// passed into the CameraIcon as `label=`. The icon is visible on mouse hover
	editAvatar: MessageDescriptor;
	editNameButtonLabel: MessageDescriptor;
	editNameInputLabel: MessageDescriptor;
};

// project-settings-apps/issue-type-page/view/field/view.js
export type RequiredFieldMessages = {
	required: MessageDescriptor;
	requiredTooltip: MessageDescriptor;
	requiredDisabledAndCheckedTooltip: MessageDescriptor;
	requiredDisabledAndUncheckedTooltip: MessageDescriptor;
	requiredDisabledInAllCategoriesTooltip: MessageDescriptor;
	requiredDisabledInCategoryTooltip: MessageDescriptor;
};

// project-settings-apps/issue-type-page/view/delete-field-modal/view.js
export type DeleteFieldMessages = {
	buttonConfirm: MessageDescriptor;
	buttonCancel: MessageDescriptor;
	modalHeader: MessageDescriptor;
	issueDataWipeWarning: MessageDescriptor;
	eventualConsistencyWarning: MessageDescriptor;
	noUndo: MessageDescriptor;
	warningWhenOneIssueType: MessageDescriptor;
	warningWhenMultipleIssueTypes: MessageDescriptor;
};

// project-settings-apps/issue-type-page/view/fields-editor/view.js
export type FieldsEditorMessages = {
	sidebarHelperText: MessageDescriptor;
	removeDropzoneHeader: MessageDescriptor;
	removeDropzoneDescription: MessageDescriptor;
	noActionFieldToolbarHeader: MessageDescriptor;
	noActionFieldToolbarDescription: MessageDescriptor;
	noActionFieldToolbarDescriptionAppField: MessageDescriptor;
	/**
	 * inactiveFieldsEmptyMessage shows when an issue type has no unused fields
	 * The list is empty and users can drag&drop fields into it
	 */
	inactiveFieldsEmptyMessage: MessageDescriptor;
	errorMessageTitle: MessageDescriptor;
	errorMessage: MessageDescriptor;
	errorMessageIssueTermRefresh?: MessageDescriptor;
	refreshButton: MessageDescriptor;
	sharedFieldInfoMessage: MessageDescriptor;
	sharedFieldDialogMessage: MessageDescriptor;
	sharedFieldLinkToDocumentationText: MessageDescriptor;
};

export type NavigationMessages = {
	issueTypesLink: MessageDescriptor;
	issueTypesLinkIssueTermRefresh?: MessageDescriptor;
	addIssueTypeDisabledTooltip: MessageDescriptor;
	addIssueTypeDisabledTooltipIssueTermRefresh?: MessageDescriptor;
};

export type CopyLayoutButtonMessages = {
	copyIssueLayout: MessageDescriptor;
};

export type BottomProductControlProps = {
	// hasJiraAdminGlobalPermissions - this variable should be true for if user has Jira admin global permissions and false otherwise
	readonly hasJiraAdminGlobalPermissions: boolean;
	readonly projectId: number;
	readonly projectKey: string;
	readonly rootContainerName: string; // This is what the products use to pass. For example, for company project this is screenName.,
	readonly rootContainerId: number; // This is what the products use to pass. For example, for team-managed project this is issueTypeId. For classic project, this is screenId.,
	/**
	 * didUserChangeAnything property is true if there are any pending, unsaved changes on the page.
	 * If false then there are no edits.
	 * If user changes something and then does the opposite change, or hits "Discard" button, then this boolean will go back to `false`
	 */
	readonly didUserChangeAnything: boolean;
};

export type ListStyleProps = {
	isDraggingOver: boolean;
	innerRef?: (element: HTMLElement | null) => void;
	dropHighlight: boolean;
	enableFullSizeDropZone?: boolean;
	children: ReactNode;
};

export type DefaultListProps<T> = {
	// Indicates if empty state should be rendered. When not defined, internal list implementation will be used for such decision.
	forceRenderEmptyState?: boolean;
	enableFullSizeDropZone?: boolean;
	/**
	 * Component that will be rendered as an empty state under conditions
	 * - forceRenderEmptyState = true: EmptyState component will be rendered
	 * - forceRenderEmptyState = false: EmptyState component will not be rendered
	 * - forceRenderEmptyState = undefined: EmptyState component will be rendered if staticItems + items are empty
	 */
	EmptyState: ComponentType<{}>;
	// Optional non-draggable items to be rendered at the top of the list
	staticItems: ReadonlyArray<T>;
	// Optional CSS; outer style that applies to whole Droppable
	ListStyle: ComponentType<ListStyleProps>;
	// Required: the minimum number of items in the list.
	// If needed, the list will be padded with invisible placeholders.
	minimumItemCount: number;
	// to simulate copy during drag
	cloneOnDrag: boolean;
	direction: Direction;
	// disable native react-beautiful-dnd placeholder
	disablePlaceholder: boolean;
	onUseDestroyAnimation: (arg1: DraggableStateSnapshot) => boolean;
	onRender: () => void;
};

export type RequiredListProps<T> = {
	// If true the it is not allowed to drag items away from this list
	isDragDisabled: boolean;
	// If true then it is not possible to drop items in this list
	isDropDisabled: boolean;
	// Not displayed; is required for drag&drop matching, is reported in onDragStart and onDragEnd
	id: string;
	// Draggables
	items: ReadonlyArray<T>;
	// Required: provide a function that renders item T
	renderItem: (field: T, isDragging: boolean, scaleX: number, transition: string) => ReactNode;
	dropHighlight: boolean;
	getDropAnimationWidth: (arg1: DraggableStateSnapshot) => number | typeof DO_NOT_ANIMATE_WIDTH;
	getItemWidth: (
		style: DraggingStyle | NotDraggingStyle | undefined,
	) => number | typeof DO_NOT_ANIMATE_WIDTH;
};

// Minimal type to allow consumers to declare a ComponentType which correctly recognises default props as optional
type MinimalListProps<T> = Partial<DefaultListProps<T>> & RequiredListProps<T>;

export type DroppableLayoutItemListProps = MinimalListProps<InactiveLayoutItem>;

export type SidebarFilterComponentProps = {
	readonly filter: LayoutItemsFilter;
	readonly totalAvailableItemsCount: number;
	readonly asyncState: AvailableLayoutItemsSidebarAsyncState;
	readonly onChange: (arg1: LayoutItemsFilter, arg2?: UIAnalyticsEvent) => void;
	readonly disabledReason?: MessageDescriptor;
};

export type ProformaRequestTypeFormCardConfig = {
	// Request type context
	requestTypeId: number;
	projectId: number;
	// Current associated form
	formId: number;
	formName: string;
	linkedFields?: {
		[key: string]: ProformaReqTypeFieldConfig;
	};
	isOptimistic: boolean;
	restrictionStatus?: FormRestrictionStatus;
};

export type MainContentProps = {
	projectKey: string;
	extraDefinerId: ExtraDefinerId | null;
	issueTypeResetCount: number;
	/**
	 * Represents all issue type validation errors. They can be rendered by main content
	 * or passed down to the children to be rendered. Which errors will be rendered by
	 * the particular component is determined by component itself, depending of "position"
	 * property value within each error object
	 */
	issueTypeValidationErrors: ValidationError[];
	activeFieldListRefElement: ReactNode;
	activeFieldCategories: FieldCategoryWithFields[];
	DescriptionInlineInput: ComponentType<{
		label?: string;
		placeholder?: string;
	}>;
	HelpTextInlineInput: ComponentType<{
		label?: string;
		placeholder?: string;
	}>;
	children: (arg1: {
		category: FieldCategoryWithFields;
		/**
		 * Indicates if empty state should be rendered for the category.
		 * When not defined, internal implementation will be used for such decision.
		 */
		forceRenderEmptyState?: boolean;
		enableFullSizeDropZone?: boolean;
	}) => ReactElement;
	fieldsDocumentationUrl: string | undefined;
	issueLayoutOwners: IssueLayoutOwner[];
	onUpdateIssueLayoutOwner: (newData: IssueLayoutOwner) => void;
	onSetHasSeenEmptyStatePulse: () => void;
	getFirstPossibleCategory: (layoutItem: LayoutItem) => FieldCategoryWithFields | undefined;
	addNewFieldsToBottom: boolean;
};

export type NewMainHeaderProps<T extends IssueLayoutOwner = IssueLayoutOwner> = {
	issueLayoutOwners: IssueLayoutOwner[];
	issueTypeResetCount: number;
	isIssueRestrictionEnabled: boolean;
	shouldShowWorkflowEditor: boolean;
	extraDefinerId: ExtraDefinerId | null;
	extraActions: ReactNode[] | null;
	project: ProjectContext;
	// those properties should be migrated to `extraActions`
	IssueTypeRestriction: ComponentType<{
		issueTypeName?: string;
	}>;
	EditWorkflowButton: ComponentType<{
		issueTypeId: IssueTypeId | undefined;
	}>;
	onUpdateIssueLayoutOwner: (newData: T) => void;
};

export type MainHeaderProps = {
	issueType: IssueType;
	issueTypeCollection: IssueTypeCollection;
	issueLayoutOwners: IssueLayoutOwner[];
	issueTypeResetCount: number;
	isEditingAvatar: boolean;
	isIssueRestrictionEnabled: boolean;
	shouldShowWorkflowEditor: boolean;
	extraDefinerId: ExtraDefinerId | null;
	selectedAvatar: Avatar;
	readonly tenant: TenantContext;
	locale: Locale;
	extraActions: ReactNode[] | null;
	project: ProjectContext;
	NameInlineInput: ComponentType<{}>;
	IssueTypeRestriction: ComponentType<{
		issueTypeName?: string;
	}>;
	DescriptionInlineInput: ComponentType<{
		label?: string;
		placeholder?: string;
	}>;
	EmailRequestTypeDescription: ComponentType<{}>;
	AvatarIcon: ComponentType<{}>;
	AvatarModal: ComponentType<{}>;
	EditWorkflowButton: ComponentType<{
		issueTypeId: IssueTypeId | undefined;
	}>;
	onUpdateIssueLayoutOwner: (newData: IssueLayoutOwner) => void;
	onAvatarSelected: (avatar: Avatar) => void;
	onDismissAvatarModal: () => void;
};

export type FieldComponentType = ComponentType<{
	isDragging: boolean;
	highlightPattern: null | RegExp;
	layoutItem: InactiveFieldLayoutItem;
	scaleX: number;
	transition: string;
	getIndexAfterUnsortableFields: (arg1: FieldCategoryWithFields, arg2: number) => number;
}>;

export type FieldGroupComponentProps = {
	isDropDisabled: boolean;
	hasNextPage: boolean;
	groupId: Group;
	// Count of all items on all pages, after filter was applied
	totalCount: number;
	items: ReadonlyArray<BaseLayoutItem>;
	pageSize: number;
	endCursor: null | string;
	droppableListIdPrefix: string;
	getDropAnimationWidth: (arg1: DraggableStateSnapshot) => number | typeof DO_NOT_ANIMATE_WIDTH;
	DroppableLayoutItemList: ComponentType<DroppableLayoutItemListProps>;
	onLoadMore: (
		groupId: Group,
		endCursor: string,
		pageSize: number,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onRenderItem: (
		field: InactiveLayoutItem,
		isDragging: boolean,
		scaleX: number,
		transition: string,
	) => ReactNode;
	title?: string;
};

export type RestrictedFieldGroupComponentProps = FieldGroupComponentProps & {
	readonly tenant: TenantContext;
	messages: {
		suggestedGroupContent: MessageDescriptor;
	};
	restrictedFieldGroupMessages: {
		label: MessageDescriptor;
		descriptionGlobalAdmin: MessageDescriptor;
		descriptionNonGlobalAdmin: MessageDescriptor;
		labelIssueTermRefresh: MessageDescriptor;
		descriptionGlobalAdminIssueTermRefresh: MessageDescriptor;
		descriptionNonGlobalAdminIssueTermRefresh: MessageDescriptor;
	};
};

export type LayoutItemsFilter = {
	name: string;
};

export type NotFoundStateProps = {
	showCreateCustomFieldsLink: boolean;
};

export type AvailableLayoutItemsListProps = {
	isDropDisabled: boolean;
	droppableListIdPrefix: string;
	itemGroups: ReadonlyArray<InactiveLayoutItemGroup>;
	productMessages: FieldsEditorMessages;
	getDropAnimationWidth: (arg1: DraggableStateSnapshot) => number | typeof DO_NOT_ANIMATE_WIDTH;
	getIndexAfterUnsortableFields: (arg1: FieldCategoryWithFields, arg2: number) => number;
	project: ProjectContext;
	FieldComponent: FieldComponentType;
	PanelComponent: ComponentType<InactiveLayoutItemPanelProps>;
	DroppableLayoutItemList: ComponentType<DroppableLayoutItemListProps>;
	filter: LayoutItemsFilter;
};

export type AvailableLayoutItemsSidebarAsyncState =
	| typeof INIT
	| typeof LOADING
	| typeof SUCCESS
	| typeof FAILURE;

export type AvailableLayoutItemsSidebarProps = {
	// DnD props
	isDropDisabled: boolean;
	isMouseDragging: boolean;
	isFieldsLimitReached: boolean;
	issueLayoutOwners: IssueLayoutOwner[];
	getIndexAfterUnsortableFields: (arg1: FieldCategoryWithFields, arg2: number) => number;
	productMessages: FieldsEditorMessages;
	droppableListIdPrefix: string;
	getDropAnimationWidth: (arg1: DraggableStateSnapshot) => number | typeof DO_NOT_ANIMATE_WIDTH;
	// entities
	screenName: string;
	totalAvailableItemsCount: number;
	itemGroups: ReadonlyArray<AvailableLayoutItemGroup>;
	filter: LayoutItemsFilter;
	project: ProjectContext;
	asyncState: AvailableLayoutItemsSidebarAsyncState;
	availableFieldTypes: FieldTypeObj[];
	// injected components
	FieldComponent: FieldComponentType;
	FieldGroupComponent: ComponentType<FieldGroupComponentProps>;
	RestrictedFieldGroupComponent: ComponentType<RestrictedFieldGroupComponentProps>;
	PanelComponent: ComponentType<InactiveLayoutItemPanelProps>;
	DroppableLayoutItemList: ComponentType<DroppableLayoutItemListProps>;
	FieldTypeList: ComponentType<{
		id: string;
		isDisabled: boolean;
		messageTooltipWhenDisabled: string;
		items: FieldTypeObj[];
		areItemsClickable: boolean;
		onFieldTypeClicked: (fieldType: FieldTypeObj) => void;
		getDropAnimationWidth: (arg1: DraggableStateSnapshot) => number | typeof DO_NOT_ANIMATE_WIDTH;
	}>;
	fieldLimitPerProject: number | null;
	onLoadMore: (
		group: Group,
		after: string,
		pageSize: number,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onCreateField: (fieldType: FieldTypeObj) => void;
	onFilterChange: (arg1: LayoutItemsFilter, analyticsEvent?: UIAnalyticsEvent) => void;
	readonly tenant: TenantContext;

	/**
	 * Configurable actions a user can do within the sidebar. This property influences the sidebar's rendering and functionality.
	 * Each action corresponds to a certain feature or capability within the sidebar.
	 *
	 * ex. canAddNewFields allows a user to see the 'admin' sidebar that displays the searchbox which gives the users
	 * the capability to search for existing fields and add them to an issue layout. When the value is false, a 'Sidebar'
	 * is rendered with a disabled searchbox and without any additional fields.
	 *
	 * Note:
	 * - This property is introduced as part of the 'DelegatedAdmin' feature, which exclusively impacts CMP projects.
	 * - For TMP project types, THIS PROPERTY WILL NOT BE UTILIZED.
	 * - The value of this property is populated through a GraphQL query retrieving LayoutConfiguration for the issue layout editor.
	 * - The implementation of this feature is being carried out by project type, meaning that their respective GraphQL queries
	 *   will be updated to fetch 'allowedActions' upon rollout.
	 *
	 * This property is nullable for two main reasons:
	 * - Firstly, it is the shared prop type used in both TMP and CMP projects. Because the 'DelegatedAdmin'
	 *   feature does not include TMP projects, there will be no 'allowedActions' that can retrieved for them.
	 * - Secondly, the rollout of this feature across different CMP project types (JSW, JSM) will happen incrementally
	 *   as each one is implemented. Until the queries for these project types are updated, they won't possess any 'allowedActions' that
	 *   can be passed to the sidebar as a property.
	 *
	 *  In the case of a CMP project where the 'DelegatedAdmin' feature is already active, providing a null value for this property
	 *  will trigger an error. However, for projects where the 'DelegatedAdmin' feature hasn't been implemented yet, this property
	 *  will be disregarded, regardless of whether it's null or not.
	 *
	 * For more information about 'AllowedActions', please see {@link AllowedActions}
	 *
	 * For more information, please see:
	 * https://hello.atlassian.net/wiki/x/0orlng (One-Pager) and
	 * https://hello.atlassian.net/wiki/x/XnO_ow ('What can users do?' with the new permissions)
	 * TODO: Remove this field, it is replaced by accessControl https://hello.jira.atlassian.cloud/browse/DAGC-114
	 */
	allowedActions: AllowedActions | null;

	/**
	 * Access control properties of a given screen. This field influences the page's rendering and functionality
	 * when editing that screen.
	 *
	 * ex. If the conjunction of the fields 'userHasEditLayoutPermission' and 'isScreenIsolated' is true the user will see
	 * the 'admin' sidebar that displays the enabled searchbox which gives the users the capability to search for
	 * existing fields and add them to an issue layout. When it is false, a 'Sidebar' is rendered with a disabled searchbox
	 * and without any additional fields.
	 *
	 * Note:
	 * - This property exclusively impacts CMP projects.
	 * - For TMP project types, THIS PROPERTY WILL NOT BE UTILIZED.
	 * - The value of this property is populated through a GraphQL query retrieving LayoutConfiguration for the issue layout editor.
	 * - The implementation of this feature is being carried out by project type, meaning that their respective GraphQL queries
	 *   will be updated to fetch 'accessControl' upon rollout.
	 */
	accessControl: AccessControl | null;
	explicitFieldAssociation: ExplicitFieldAssociationSettings;
};

export type FieldConfigurationComponentProps = {
	/**
	 * List of all layout owners that use this field.
	 * It's going to be non-empty (because if it was empty then the SWIFT page would have nothing to render in the first place).
	 * The order is arbitrary. To find the current one use `currentIssueLayoutOwnerId`.
	 */
	layoutOwners: IssueLayoutOwner[];
	/**
	 * The owner that is currently displayed on the screen.
	 * It's going to match an ID in one of `layoutOwners`.
	 */
	extraDefinerId: ExtraDefinerId | null;
	projectId: ProjectId;
	projectKey: ProjectKey;
	field: AssociatedField;
	fieldErrors: ReadonlyArray<ValidationError>;

	onUpdate: (field: AssociatedField) => void;
	limits: Limits;
	/**
	 * @deprecated
	 * @default 300
	 * A workaround for VR test limitation: https://hello.atlassian.net/wiki/spaces/EPAM/pages/2874635587/VR-Tests+Work+Arounds+for+Tooltip+VR+Testing
	 * In production, it is safe to ignore this and leave the default of 300ms.
	 */
	tooltipDelay?: number;
	/**
	 * @deprecated
	 * A workaround for VR test limitation: https://hello.atlassian.net/wiki/spaces/EPAM/pages/2874635587/VR-Tests+Work+Arounds+for+Tooltip+VR+Testing
	 * In production, it is safe to ignore this.
	 */
	testId?: string;
};

export type SidebarContainerProps = {
	children: ReactNode;
};

/**
 * A definition of how a field card behaves.
 * Platform has some basic opinion (styling, changing name, drag&drop, etc) and configuration via operations.
 * Products can decide which AssociatedField renders which FieldCard,
 * and they can define individual ConfigurationComponents too.
 */
export type FieldCard = {
	/**
	 * This function returns a recommendation for how to render a field card.
	 * If it returns true, a card is always expandable.
	 * If it returns false, card may still expand if the SWIFT platform code wishes to render something inside
	 * and wants to make sure to have enough space.
	 *
	 * @param operations
	 * @returns true: always expand; false: sometimes expand
	 */
	isExpandable: (operations: FieldOperations) => boolean;
	isFieldDescriptionApplicable?: boolean;
	ConfigurationComponent?: ComponentType<FieldConfigurationComponentProps>;
};

export type IssueLayoutBreadcrumbConfig = {
	/**
	 * This href will be passed to Breadcrumb from @atlassian/jira-breadcrumbs
	 */
	getHref: (projectType: ProjectType, projectKey: string) => string;
	/**
	 * This will be translated and displayed to users as a link text
	 */
	message: MessageDescriptor;
	/**
	 * This string will be passed to analytics
	 */
	type: string;
};

export type GetFieldTooltipContent = (params: {
	readonly fieldCreator: string;
	readonly isAppField: boolean;
	/**
	 * @deprecated instead use associatedField.operations.editable
	 */
	readonly isEditable: boolean;
	readonly associatedField: AssociatedField;
	readonly isRequirabilityDisplayed: boolean;
}) => ReactNode | null;

/**
 * Errors show first, then warnings, then infos.
 */
export type ActiveFieldHeaderInfoLevel = 'info' | 'warning' | 'error';

export type ActiveFieldHeaderInfo = {
	/**
	 * This will be the lozenge label, recommended to be a single word.
	 * Will not be displayed on smaller screens.
	 */
	shortText?: MessageDescriptor;
	/**
	 * This will be the lozenge tooltip
	 */
	midText?: string;
	/**
	 * Icon to be displayed on the right hand side of FieldAssociated component before chevron icon
	 * (if the field is expandable).
	 *
	 * The `type` of the icon also determines how important it is.
	 */
	icon?: {
		type: ActiveFieldHeaderInfoLevel;
		/** Accessible label for icon indicating its meaning */
		label: MessageDescriptor;
	};
	/**
	 * Content to be rendered inside expandable body of FieldAssociated component.
	 * Also plays as condition for FieldAssociated to expand: if passed, a field would expand.
	 */
	longText?: ReactNode;
};

export type GetActiveFieldHeaderInfoParams = {
	readonly associatedField: AssociatedField;
	readonly intl: IntlShape;
	readonly layoutOwners: ReadonlyArray<IssueLayoutOwner>;
	readonly isRequirabilityDisplayed: boolean;
	readonly projectContext: {
		readonly projectId: ProjectId;
		readonly projectKey: ProjectKey;
		readonly projectType: ProjectType;
	};
};

/**
 * If this returns undefined then platform code will not render anything.
 */
export type GetActiveFieldHeaderInfo = (
	params: GetActiveFieldHeaderInfoParams,
) => ActiveFieldHeaderInfo | undefined;

/**
 * Feedback collector is where end users can type their comments and feelings and send it to us.
 * It requires no authorization or authentication so don't use it for anything sensitive.
 *
 * All feedback is dumped into a Jira project https://jira.atlassian.com/projects/FEEDBACK and is available to view for all Atlassian employees.
 */
export type FeedbackCollectorConfig = {
	/**
	 * Used to group feedbacks from different collectors.
	 * All feedbacks from collectors with id `project-settings.issue-type` can be grouped together
	 * by `feedbackContext` entry `feedbackGroupId=project-settings.issue-type`.
	 */
	feedbackGroupId: string;
	/**
	 * Identification for feedback collector based on its location in the product.
	 * For instance, we can find all feedbacks for Swift TMP page based on source `jsw-tmp-issue-type-form`.
	 */
	source: string;
};

export type FieldToolbarNoActionProps = {
	issueTypeName: string;
	isAppField: boolean;
	draggingField: AssociatedField | Field | undefined;
	fieldCreator: string;
	messages: FieldsEditorMessages;
	intl: IntlShape;
	layoutOwners: IssueLayoutOwner[];
	extraDefinerId: number | null;
};

/**
 * Check this page for details: https://hello.atlassian.net/wiki/spaces/JPLAT/pages/377923997/Product+features
 */
export type ProductFeatures = Readonly<{
	// Create issue type plus navigation section
	issueTypeHierarchy: boolean;
	/**
	 * If this is defined then the left navigation bar shows a "+ Add Issue type" button with a modal dialog
	 * addIssueTypeMessages: undefined means that the product does not allow creating new issue types
	 */
	addIssueTypeMessages: AddIssueTypeMessages | undefined;
	issueTypeTemplates: TemplateIntl[];
	navigationMessages: NavigationMessages;
	// Main header section
	MainHeader: ComponentType<MainHeaderProps>;
	/**
	 * If true, then value of helptext box is added to issueType.properties when saving changes
	 */
	issueTypeHelpText: boolean;
	workflowEditor: boolean;
	issueRestriction: boolean;
	/**
	 * null means that SWIFT is not going to render breadcrumbs at all.
	 *
	 * If this is defined then SWIFT renders full-sized breadcrumbs, going:
	 * Project > "Current project" > Settings > "Link text"
	 *
	 * Both the Link text and Link URL are coming from inside the IssueLayoutBreadcrumbConfig.
	 * Only the last bit is configurable. "Project > Current project > Settings" are always the same.
	 */
	issueLayoutBreadcrumbConfig: IssueLayoutBreadcrumbConfig | null;
	/**
	 * This is for when users did something wrong and must correct their mistake before saving changes.
	 * Empty array:
	 * - the issue data is valid as far as cards are concerned
	 * - happy path
	 * - users can save
	 * Array with at least one string fieldKey in it:
	 * - the issue data for at least one card is invalid
	 * - "Save changes" button is still active but instead of saving, it scrolls to the error
	 * users must correct the error before saving.
	 *
	 * Errors identified by this function take priority over those identified by `validateField`,
	 * when scrolling to the first card with an error.
	 *
	 * This function is expected to return an array of the keys that are wrong.
	 * SWIFT is not going to attempt to understand the keys, it will only check
	 * if the array has something in it. If so it will block saving and pass
	 * the key back as `forceValidationFor`.
	 */
	validateIssueDataCards: (arg1: {
		issueDataState: IssueDataState;
		issueLayoutOwners: IssueLayoutOwner[];
	}) => ReadonlyArray<string>;
	// Field configuration section
	fieldCategoryConfiguration: FieldCategoryConfiguration[];
	MainContent: ComponentType<MainContentProps>;
	Scrollable: ComponentType<{
		children?: ReactNode;
	}>;
	Footer: ComponentType<{
		children?: ReactNode;
	}>;
	fieldAlias: boolean;
	allowRequiringFields: boolean;
	addNewFieldsToBottom: boolean;
	fieldsEditorMessages: FieldsEditorMessages;
	requiredFieldMessages: RequiredFieldMessages | undefined;
	/**
	 * @deprecated This property is only ever used in MainContent component anyway; instead of passing it through platform code, just use it there.
	 */
	fieldsDocumentationUrl: string | undefined;
	/**
	 * Renders messages inside the field card.
	 * If you wish to split this into smaller functions then check out `mergeGetActiveFieldHeaderInfo` in product-features.
	 */
	getActiveFieldHeaderInfo: GetActiveFieldHeaderInfo;
	/**
	 * Defines which field type renders which Field card.
	 * Mapping example: "com.atlassian.jira.plugin.system.customfieldtypes:float" should return mapping for "NumberFieldCard".
	 *
	 * For some fieldType strings see: src/packages/project-settings/common/src/consts.js
	 * Although these are wildly dynamic and can be defined by plugins and overall not something that we can put an enum on.
	 *
	 * If unsure how to treat a fieldType then return null.
	 * It will default into "unknown and unsupported" Field card - one that does not open and does not show any interesting information inside.
	 */
	getFieldCard: (fieldType: string) => FieldCard | null;
	/**
	 * A function to get the screen id given a particular issue type and layout owners.
	 * This is used to construct the url for the "edit fields and tabs" button.
	 */
	getScreenId: (arg: {
		issueType: IssueType;
		issueLayoutOwners: IssueLayoutOwner[];
	}) => number | undefined;
	/**
	 * Function that validates fields.
	 * This is for when users did something wrong and must correct their mistake before saving changes.
	 * Empty array:
	 * - field is valid
	 * - happy path
	 * - users can save
	 * Array with at least one ValidationError in it:
	 * - card chevron is replaced with red error icon
	 * - error renders inside the Field card
	 * - "Save changes" button is still active but instead of saving, it scrolls to the error
	 * - users must correct the error before saving.
	 *
	 * There is a limit of 10 ValidationErrors maximum. SWIFT will only consider the first 10, and ignore all others.
	 * The errors must have unique messages. Duplicates will be discarded. If a message is duplicate or not
	 * is determined by messageDescriptor.id only. The limit is applied before duplicate check.
	 *
	 * Field validation is on a hot code path, and it is sensitive performance-wise. It will be called on every render, for every field.
	 * For this reason, SWIFT will memoize return value from this function.
	 * Make sure that its results are deterministic - i.e. the same field instance returns the same error.
	 * And even with that - please do not do any network calls. Or JSON stringify. Or anything that can take 1 ms or more.
	 * This is really only good for null checks, or string length, etc.
	 *
	 * Check out and reuse some premade field validators in ./utils/validation/field or write your own
	 */
	validateField: FieldValidator;
	/**
	 * Function that validates issue type.
	 * @param   {IssueDataState} issueDataState - issue type data to validate
	 * @param   {string[]} existingIssueTypeNames - names of existing issue types
	 * @param   {IssueLayoutOwner[]} issueLayoutOwners - issue layout owners to validate
	 * @returns {ValidationError[]} - empty array if no errors, otherwise array with errors
	 */
	validateIssueType: (arg: {
		issueDataState: IssueDataState;
		existingIssueTypeNames: string[];
		issueLayoutOwners: IssueLayoutOwner[];
	}) => ValidationError[];
	/**
	 * If the function is null it means no tooltip will be rendered
	 * */
	getFieldTooltipContent: GetFieldTooltipContent;
	BottomProductControl: ComponentType<BottomProductControlProps>;
	/**
	 * if value is set to `null` no feedback button will be rendered.
	 * All feedback go to this project https://customerfeedback.atlassian.net/jira/servicedesk/projects/JGF/queues/custom/448
	 * */
	feedbackCollectorConfig: FeedbackCollectorConfig | null;
	editIssueTypeMessages: EditIssueTypeMessages;
	// Sidebar, AKA "Right-Hand Panel" section
	/**
	 * i18n messages needed to render all experiences related to deleting a custom field.
	 * If this is undefined, then SWIFT UI never allows deleting any fields!
	 */
	deleteFieldMessages: DeleteFieldMessages | undefined;
	AvailableLayoutItemsSidebar: ComponentType<AvailableLayoutItemsSidebarProps>;
	// Data loading and saving section
	fetchAvailableItemsService: FetchAvailableItemsService;
	fetchMoreAvailableItemsService: FetchMoreAvailableItemsService;
	fetchLayoutConfiguration: FetchLayoutConfigurationService;
	issueLayoutType: 'customer' | 'agent' | 'issueView' | 'issueCreate';
	/**
	 * This component, if defined, will be rendered after click on "Save changes" and after successful validation
	 * but before calling the "save data service".
	 * This component will have `onCancel` and `onConfirm` props which will typically be passed inside a modal window
	 * or something similar.
	 *
	 * SWIFT will render this component and then wait until one of those two callbacks are called
	 * before continuing - or aborting - the save.
	 *
	 * If this is `null` then nothing is rendered and SWIFT will continue saving. Same as if calling `onConfirm` immediately.
	 */
	ConfirmBeforeSave: ComponentType<ConfirmBeforeSaveProps> | null;
	/**
	 * SWIFT will use this config to create events and pass them to @atlassian/browser-metrics library.
	 * This data should then become available in http://go/performanceportal
	 *
	 * The convention is that they metric keys will start with a prefix `project-settings.issue-type`
	 * but there is nothing enforcing that.
	 *
	 * The metrics are passed as-is to the library.
	 * There is no additional processing, or prefixes, or suffixes done ----> by SWIFT <----.
	 *
	 * The metric key should NOT start with `jira.fe.page-load` because the library does that internally.
	 * Here's details: https://hello.atlassian.net/wiki/spaces/APD/pages/1314070214/HOWTO+add+new+metric+to+perf+portal
	 */
	metricsConfig: {
		pageLoad: PageLoadConfig;
	};
	saveChangesService: SaveChangesService;
	swiftVersionForAnalytics: SwiftVersion;
	SidebarContainer: ComponentType<SidebarContainerProps>;
	FieldToolbarNoAction: ComponentType<FieldToolbarNoActionProps>;
	bottomControlsIsDirty?: boolean;
	bottomControlsIsSaving?: boolean;
	bottomControlsDiscardChanges?: () => void;
}>;

export const PACKAGE_NAME: PackageName = toPackageName('jiraProjectSettingsAppsIssueTypePage');
